import React, { useState, useMemo, useEffect } from 'react';
import { AI_TOOLS } from '../constants';
import { CATEGORIES, CATEGORY_STRUCTURE, TAG_MAPPING } from '../categoryData';
import { Tool } from '../types';
import { Link } from 'react-router-dom';


interface MarketplaceProps {
  onEnableTool: (toolId: string) => void;
  onDisableTool: (toolId: string) => void;
  activeToolIds: string[];
}

const Marketplace: React.FC<MarketplaceProps> = ({ onEnableTool, onDisableTool, activeToolIds }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubCategory, setSelectedSubCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewTab, setViewTab] = useState<'all' | 'active'>('all');
  const [visibleCount, setVisibleCount] = useState(60);

  // Helper to normalize category strings (remove emojis and numbering)
  const normalize = (s: string | undefined | null) => {
    if (!s || typeof s !== 'string') return '';
    return s.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').replace(/^\d+\.\s*/, '').trim();
  };

  const cleanDisplay = (s: string) => s.replace(/\s*\(.*?\)\s*/g, '').trim();

  // Helper to check match
  const matches = (toolValue: string | string[] | undefined | null, selectedValue: string, needsNormalization = false) => {
    if (!toolValue) return false;
    const values = Array.isArray(toolValue) ? toolValue : [toolValue];
    return values.some(v => (needsNormalization ? normalize(v) : v) === selectedValue);
  };

  // Main filtering logic: Consolidated into one source of truth
  const filteredTools = useMemo(() => {
    let list = AI_TOOLS;

    // 1. Tab Filter (Discover vs Active)
    if (viewTab === 'active') {
      list = list.filter(t => activeToolIds.includes(t.id));
    }

    // 2. Main Category Filter
    if (selectedCategory !== 'All') {
      if (selectedCategory === 'Free') {
        list = list.filter(tool => tool.creditCost === 0);
      } else {
        list = list.filter(tool => matches(tool.category, selectedCategory, true));
      }
    }

    // 3. SubCategory Filter
    if (selectedSubCategory !== 'All') {
      list = list.filter(tool => matches(tool.subCategory, selectedSubCategory));
    }

    // 4. Search Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(tool => {
        const cats = Array.isArray(tool.category) ? tool.category : [tool.category];
        const subcats = Array.isArray(tool.subCategory) ? tool.subCategory : [tool.subCategory];

        return tool.name.toLowerCase().includes(q) ||
          tool.description.toLowerCase().includes(q) ||
          cats.some(c => c && c.toLowerCase().includes(q)) ||
          subcats.some(s => s && s.toLowerCase().includes(q));
      });
    }

    return list;
  }, [viewTab, selectedCategory, selectedSubCategory, searchQuery, activeToolIds]);

  const { allCount, activeCount } = useMemo(() => {
    // Note: Counters should reflect the selection of categories/subcategories
    // to match the user's expected behavior of "drilling down".
    return {
      allCount: filteredTools.length,
      activeCount: filteredTools.filter(t => activeToolIds.includes(t.id)).length
    };
  }, [filteredTools, activeToolIds]);

  // Categories present in the current tabTools (unfiltered by cat/subcat)
  const visibleCategories = useMemo(() => {
    const list = viewTab === 'active'
      ? AI_TOOLS.filter(t => activeToolIds.includes(t.id))
      : AI_TOOLS;

    const activeCats = new Set<string>();
    list.forEach(tool => {
      const cats = Array.isArray(tool.category) ? tool.category : [tool.category];
      cats.forEach(c => activeCats.add(normalize(c)));
    });

    const hasFree = list.some(t => t.creditCost === 0);

    return CATEGORIES.filter(cat => {
      if (cat === 'All') return true;
      if (cat === 'Free') return hasFree;
      return activeCats.has(cat);
    });
  }, [viewTab, activeToolIds]);

  // Subcategories present in the current category/tab selection
  const availableSubcategories = useMemo(() => {
    const listWithCategory = (viewTab === 'active' ? AI_TOOLS.filter(t => activeToolIds.includes(t.id)) : AI_TOOLS).filter(tool => {
      if (selectedCategory === 'All') return true;
      if (selectedCategory === 'Free') return tool.creditCost === 0;
      return matches(tool.category, selectedCategory, true);
    });

    const subItems = new Set<string>();
    listWithCategory.forEach(tool => {
      const subs = Array.isArray(tool.subCategory) ? tool.subCategory : [tool.subCategory];
      subs.forEach(s => {
        const n = normalize(s);
        if (n) subItems.add(n);
      });
    });

    const allCanonical = CATEGORY_STRUCTURE.flatMap(c => c.subcategories);
    const canonicalInSet = allCanonical.filter(s => subItems.has(s));
    const nonCanonicalInSet = Array.from(subItems).filter(s => !allCanonical.includes(s));

    return Array.from(new Set([...canonicalInSet, ...nonCanonicalInSet]));
  }, [viewTab, activeToolIds, selectedCategory]);

  // Reset selections if they disappear
  useEffect(() => {
    if (!visibleCategories.includes(selectedCategory)) {
      setSelectedCategory('All');
      setSelectedSubCategory('All');
    }
  }, [visibleCategories, selectedCategory]);

  useEffect(() => {
    if (selectedSubCategory !== 'All' && !availableSubcategories.includes(selectedSubCategory)) {
      setSelectedSubCategory('All');
    }
  }, [availableSubcategories, selectedSubCategory]);

  const displayedTools = filteredTools.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 60);
  };

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      {/* Header & Navigation */}
      {/* Header & Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* View Tabs */}
        <div className="flex p-1 bg-slate-200 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-800 rounded-xl w-fit shadow-inner shrink-0">
          <button
            onClick={() => { setViewTab('all'); setVisibleCount(60); }}
            className={`flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${viewTab === 'all'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
          >
            <span>🧩 Discover Library</span>
            <span className={`ml-2 px-2 py-0.5 rounded-md text-[10px] ${viewTab === 'all' ? 'bg-white/20 text-white' : 'bg-slate-300 dark:bg-slate-800 text-slate-600 dark:text-slate-500'}`}>
              {allCount}
            </span>
          </button>
          <button
            onClick={() => { setViewTab('active'); setVisibleCount(60); }}
            className={`flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${viewTab === 'active'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full shadow-[0_0_8px_rgba(163,230,53,0.5)] ${viewTab === 'active' ? 'bg-white shadow-none' : 'bg-lime-400'}`} />
              <span>My Active Tools</span>
            </div>
            <span className={`ml-2 px-2 py-0.5 rounded-md text-[10px] ${viewTab === 'active' ? 'bg-white/20 text-white' : 'bg-slate-300 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
              {activeCount}
            </span>
          </button>
        </div>

        {/* Search */}
        <div className="flex-1 max-w-xl relative group">
          <input
            type="text"
            placeholder="Search AI tools..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(60); }}
            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm shadow-sm group-hover:shadow-md"
          />
          <svg className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
      </div>

      <div className="flex flex-col gap-4">

        {/* Row 1: Main Categories */}
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-800 overflow-x-auto no-scrollbar whitespace-nowrap">
          {visibleCategories.map(category => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setSelectedSubCategory('All');
                setVisibleCount(60);
              }}
              className={`flex-shrink-0 px-4 py-2 text-xs font-bold rounded-lg transition-all ${selectedCategory === category
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border dark:border-slate-800 border-slate-200 hover:border-indigo-400 dark:hover:border-slate-600 shadow-sm'
                }`}
            >
              {cleanDisplay(category)}
            </button>
          ))}
        </div>

        {/* Row 2: Subcategories */}
        {availableSubcategories.length > 0 && (
          <div className="flex items-center gap-2 animate-in slide-in-from-top-2 duration-300 overflow-x-auto no-scrollbar whitespace-nowrap">
            {availableSubcategories.map(sub => (
              <button
                key={sub}
                onClick={() => {
                  // Toggle logic: If clicking active sub, revert to All. Else select sub.
                  setSelectedSubCategory(selectedSubCategory === sub ? 'All' : sub);
                  setVisibleCount(60);
                }}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded-full transition-all ${selectedSubCategory === sub
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-slate-100 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border dark:border-slate-800 border-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
              >
                {cleanDisplay(sub)}
              </button>
            ))}
          </div>
        )}
      </div>


      {/* Grid of Tools */}
      {displayedTools.length > 0 ? (
        <div className="space-y-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedTools.map(tool => (
              <div key={tool.id} className={`bg-white dark:bg-slate-900/50 border rounded-2xl p-5 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all group relative overflow-hidden ${activeToolIds.includes(tool.id) ? 'border-indigo-500 ring-1 ring-indigo-500/20' : 'border-slate-200 dark:border-slate-800'}`}>
                <div className="mb-4">
                  <div className="flex justify-between items-start h-12">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-3xl group-hover:scale-110 transition-transform shadow-sm shrink-0">
                        {tool.icon}
                      </div>
                      <span className="mt-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 text-[9px] font-bold rounded-full uppercase tracking-wider whitespace-nowrap truncate max-w-full">
                        {(() => {
                          // Flatten values
                          const subCats = Array.isArray(tool.subCategory) ? tool.subCategory : [tool.subCategory];
                          const rawCats = Array.isArray(tool.category) ? tool.category : [tool.category];

                          // Determine best key based on active filters
                          let bestKey = '';

                          // A. If specific subcategory selected and tool belongs to it -> use it
                          if (selectedSubCategory !== 'All' && subCats.some(s => normalize(s) === selectedSubCategory)) {
                            bestKey = selectedSubCategory;
                          }
                          // B. If specific main category selected and tool belongs -> try to find a subcat that fits, or use main
                          else if (selectedCategory !== 'All' && selectedCategory !== 'Free' && rawCats.some(c => normalize(c) === selectedCategory)) {
                            // Try to find a subcat in this main category? Or just show the main category short tag?
                            // Actually, it's better to show the subcat tag if possible.
                            bestKey = subCats[0] || normalize(rawCats[0]);
                          }
                          // C. Default: First subcat or First category
                          else {
                            bestKey = subCats[0] || normalize(rawCats[0]);
                          }

                          // Lookup mapping
                          return TAG_MAPPING[bestKey] || TAG_MAPPING[normalize(bestKey)] || bestKey;
                        })()}
                      </span>
                    </div>
                    <div className="h-12 flex flex-col items-end justify-start gap-0.5 min-w-[85px] text-right">
                      <span className="mt-1 text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-tighter leading-none whitespace-nowrap">
                        {tool.creditCost === 0 ? 'FREE' : `${tool.creditCost} CREDITS`}
                      </span>
                      <div className="flex-1 flex items-center justify-end -mt-1">
                        {activeToolIds.includes(tool.id) && (
                          <span className="px-2.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded-full uppercase tracking-wider whitespace-nowrap">Enabled</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4 flex-1">
                  <h4 className="font-bold text-slate-900 dark:text-white text-base mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors leading-tight">
                    {tool.name}
                  </h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs line-clamp-2 leading-relaxed mb-4">
                    {tool.description}
                  </p>

                  {/* Stats Row */}
                  <div className="flex items-center justify-end space-x-6 pt-3 border-t border-slate-100 dark:border-slate-800/50" title="">
                    <div className="flex items-center gap-2" title="Activations">
                      <div className="w-2 h-2 rounded-full bg-lime-400 shadow-[0_0_8px_rgba(163,230,53,0.5)] cursor-default" />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{tool.statistics?.activations.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center gap-1.5" title="Executions">
                      <span className="text-[11px] leading-none mb-0.5 cursor-default">⚡</span>
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{tool.statistics?.totalExecutions.toLocaleString() || '0'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <Link
                    to={`/app/tools/${tool.id}`}
                    className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold rounded-lg text-center transition-colors shadow-sm"
                  >
                    DETAILS
                  </Link>
                  {activeToolIds.includes(tool.id) ? (
                    <button
                      onClick={() => onDisableTool(tool.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-red-600/20"
                    >
                      DISABLE
                    </button>
                  ) : (
                    <button
                      onClick={() => onEnableTool(tool.id)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-indigo-600/20"
                    >
                      ENABLE
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {visibleCount < filteredTools.length && (
            <div className="flex justify-center pb-8">
              <button
                onClick={handleLoadMore}
                className="px-8 py-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-500 dark:hover:border-indigo-400 text-slate-900 dark:text-white font-black text-xs rounded-2xl shadow-sm transition-all flex items-center space-x-3 uppercase tracking-widest hover:shadow-lg active:scale-95"
              >
                <span>Load More Tools</span>
                <span className="opacity-50">•</span>
                <span className="text-indigo-600 dark:text-indigo-400">{filteredTools.length - visibleCount} Remaining</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="py-32 text-center">
          <div className="text-6xl mb-6">🏜️</div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No tools in this view</h3>
          <p className="text-slate-500 max-w-sm mx-auto">Try adjusting your filters or search query.</p>
          {viewTab === 'active' && activeCount === 0 && (
            <button
              onClick={() => { setViewTab('all'); setVisibleCount(60); }}
              className="mt-6 text-indigo-600 font-bold hover:underline"
            >
              Browse the library to enable tools
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Marketplace;
